import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Rootstate } from '../../../store';
import { IonIcon } from '@ionic/react';
import { TurnOrder } from '../../../reducers/map/mapReducer';
import { useDispatch } from 'react-redux';
import { setActiveTurn, setTurnOrder } from '../../../reducers/map/mapReducerActions';
import { checkmarkOutline, trashOutline } from 'ionicons/icons';
import ModalHeader from '../../modal/ModalHeader';
import Modal from '../../modal/Modal';
import ModalBody from '../../modal/ModalBody';
import Button from '../../UI/Button';
import TableRow from '../../UI/TableRow';
import { updatePartialMapOnServer } from '../../../utils/updatePartialMapOnServer';
import Input from '../../UI/Input';

const TurnOrderWidget = ({ canEdit }: { canEdit: boolean }) => {
    const { turnOrder, activeTurn, mapId } = useSelector(
        (state: Rootstate) => ({
            turnOrder: state.map.map?.turnOrder,
            activeTurn: state.map.map?.activeTurn,
            mapId: state.map.mapId,
        }),
        shallowEqual,
    );

    const dispatch = useDispatch();

    const filteredSortedTurnOrder = turnOrder?.filter(
        (item) => canEdit || item.private !== true,
    ) ?? [];

    const [showDelete, setShowDelete] = useState(false);
    const [newEntry, setNewEntry] = useState<{ name: string; roll?: number; private: boolean } | null>(null);
    const [editingEntry, setEditingEntry] = useState<{ identifier: string; field: 'name' | 'roll' } | null>(null);
    const [tempTurnOrder, setTempTurnOrder] = useState<TurnOrder[] | null>(null);

    if (!canEdit && (turnOrder?.length ?? 0) <= 0) {
        return null;
    }

    const setNewTurnOrder = (val: TurnOrder[]) => {
        const sanitizedTurnOrder = val.map((entry) => ({
            ...entry,
            roll: entry.roll === undefined ? 0 : entry.roll, // Ensure roll is 0 if undefined
        }));
        dispatch(setTurnOrder(sanitizedTurnOrder));
        updatePartialMapOnServer({ turnOrder: sanitizedTurnOrder }, mapId);
    };

    const handleRemoveEntry = (val: string) => {
        const newTurnOrder = (turnOrder ?? [])
            .filter((item) => item.identifier !== val)
            .sort((a, b) => b.roll - a.roll) ?? [];
        setNewTurnOrder(newTurnOrder);
    };

    const toggleEntryPrivate = (val: string) => {
        const newTurnOrder = (turnOrder ?? [])
            .map((entry) =>
                entry.identifier === val
                    ? { ...entry, private: !entry.private }
                    : entry,
            )
            .sort((a, b) => b.roll - a.roll) ?? [];
        setNewTurnOrder(newTurnOrder);
    };

    const handleNextTurn = () => {
        const turnIndex = turnOrder?.findIndex((val) => val.identifier === activeTurn) ?? 0;
        const newIndex = (turnIndex + 1) % (turnOrder ?? []).length;
        const newId = turnOrder?.[newIndex]?.identifier ?? undefined;
        dispatch(setActiveTurn(newId));
        updatePartialMapOnServer({ activeTurn: newId }, mapId);
    };

    const handleClearTurnOrder = () => {
        const newTurnOrder = [] as TurnOrder[];
        dispatch(setTurnOrder(newTurnOrder));
        setNewTurnOrder(newTurnOrder);
    };

    const handleConfirmNewEntry = () => {
        if (newEntry) {
            const newTurnOrder = [
                ...(turnOrder ?? []),
                { ...newEntry, roll: newEntry.roll ?? undefined, identifier: Date.now().toString() } as TurnOrder,
            ].sort((a, b) => b.roll - a.roll);
            setNewTurnOrder(newTurnOrder);
            setNewEntry(null);
        }
    };


    const handleTempUpdateEntry = (identifier: string, field: 'name' | 'roll', value: string | number) => {
        const updatedTurnOrder = (tempTurnOrder ?? turnOrder ?? []).map((entry) =>
            entry.identifier === identifier
                ? {
                    ...entry,
                    [field]: field === 'roll' && value === '' ? undefined : field === 'roll' ? Number(value) : value,
                }
                : entry,
        );
        setTempTurnOrder(updatedTurnOrder);
    };

    const handleBlurUpdateEntry = () => {
        const updatedTurnOrder = (tempTurnOrder ?? turnOrder ?? [])
            .map((entry) => ({
                ...entry,
                roll: entry.roll === undefined ? 0 : entry.roll, // Ensure roll is 0 if undefined
            }))
            .sort((a, b) => b.roll - a.roll) ?? [];
        setNewTurnOrder(updatedTurnOrder); // Sync with the server
        setTempTurnOrder(null); // Clear temporary state
        setEditingEntry(null); // Exit editing mode
    };

    return (
        <>
            <div
                className={
                    'flex flex-col items-stretch bg-deep-blue rounded-lg pl-3 pr-3 pt-3 pb-3' +
                    ' absolute left-6 bottom-6 flex flex-col items-center gap-y-4' +
                    ` ${canEdit ? 'w-72' : 'w-52'}`
                }
            >
                {(filteredSortedTurnOrder.length > 0 || newEntry) && (
                    <table className="table-fixed w-full">
                        <thead>
                            <tr>
                                <th className="w-1/3">Name</th>
                                <th className="w-1/3">Roll</th>
                                {canEdit && (
                                    <>
                                        <th className="w-1/6">Private</th>
                                        <th className="w-1/6">-</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSortedTurnOrder?.map((item) => (
                                <TableRow
                                    key={item.identifier}
                                    className={item.identifier === activeTurn ? 'highlighted' : ''}
                                >
                                    <td
                                        className={`truncate ${
                                            editingEntry?.identifier !== item.identifier ||
                                            editingEntry.field !== 'name'
                                                ? 'cursor-pointer'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (!editingEntry)
                                                setEditingEntry({ identifier: item.identifier, field: 'name' });
                                        }}
                                    >
                                        {editingEntry?.identifier === item.identifier &&
                                        editingEntry.field === 'name' ? (
                                                <Input
                                                    type="text"
                                                    className="w-full"
                                                    autoFocus
                                                    value={
                                                        (tempTurnOrder ?? turnOrder)?.find(
                                                            (entry) => entry.identifier === item.identifier,
                                                        )?.name ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        handleTempUpdateEntry(item.identifier, 'name', e.target.value)
                                                    }
                                                    onBlur={handleBlurUpdateEntry}
                                                />
                                            ) : (
                                                <span>{item.name}</span>
                                            )}
                                    </td>
                                    <td
                                        className={`truncate ${
                                            editingEntry?.identifier !== item.identifier ||
                                            editingEntry.field !== 'roll'
                                                ? 'cursor-pointer'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (!editingEntry)
                                                setEditingEntry({ identifier: item.identifier, field: 'roll' });
                                        }}
                                    >
                                        {editingEntry?.identifier === item.identifier &&
                                        editingEntry.field === 'roll' ? (
                                                <Input
                                                    type="text"
                                                    className="w-full"
                                                    autoFocus
                                                    value={
                                                        (tempTurnOrder ?? turnOrder)?.find(
                                                            (entry) => entry.identifier === item.identifier,
                                                        )?.roll ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        handleTempUpdateEntry(item.identifier, 'roll', e.target.value)
                                                    }
                                                    onBlur={handleBlurUpdateEntry}
                                                />
                                            ) : (
                                                <span>{item.roll}</span>
                                            )}
                                    </td>
                                    {canEdit && (
                                        <>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={item.private}
                                                    onClick={() => {
                                                        toggleEntryPrivate(item.identifier);
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <button onClick={() => handleRemoveEntry(item.identifier)}>
                                                    <IonIcon size="md" icon={trashOutline} />
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                            {newEntry && (
                                <TableRow>
                                    <td>
                                        <Input
                                            type="text"
                                            className="w-full"
                                            autoFocus
                                            value={newEntry.name}
                                            onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                                            placeholder="Name"
                                        />
                                    </td>
                                    <td>
                                        <Input
                                            type="number"
                                            className="w-full"
                                            value={newEntry.roll === undefined ? '' : newEntry.roll}
                                            onChange={(e) =>
                                                setNewEntry({
                                                    ...newEntry,
                                                    roll: e.target.value === ''
                                                        ? undefined
                                                        : Number(e.target.value),
                                                })
                                            }
                                            placeholder="Roll"
                                        />
                                    </td>
                                    <td>
                                        <Input
                                            type="checkbox"
                                            checked={newEntry.private}
                                            onChange={(e) => setNewEntry({ ...newEntry, private: e.target.checked })}
                                        />
                                    </td>
                                    <td>
                                        <button onClick={handleConfirmNewEntry}>
                                            <IonIcon size="md" icon={checkmarkOutline} />
                                        </button>
                                    </td>
                                </TableRow>
                            )}
                        </tbody>
                    </table>
                )}
                {canEdit && (
                    <div
                        className={
                            filteredSortedTurnOrder.length > 0
                                ? 'border-t border-t-mid-blue flex gap-2 pt-2'
                                : 'flex gap-2'
                        }
                    >
                        <Button onClick={() => handleNextTurn()}>next</Button>
                        <Button
                            onClick={() => setNewEntry({ name: '', roll: 0, private: false })}
                            disabled={!!newEntry}
                        >
                            add
                        </Button>
                        <Button onClick={() => handleClearTurnOrder()}>clear all</Button>
                    </div>
                )}
            </div>
            <Modal show={showDelete} toggle={() => setShowDelete(!showDelete)}>
                <ModalHeader toggle={() => setShowDelete(!showDelete)}>Clear turn order</ModalHeader>
                <ModalBody>
                    <button
                        className="block mb-3 w-fit pl-3 pr-3 p-2 rounded-md bg-gray-700 hover:bg-gray-500"
                        onClick={() => setShowDelete(!showDelete)}
                    >
                        Cancel
                    </button>
                    <button
                        className="block mb-3 w-fit pl-3 pr-3 p-2 rounded-md bg-gray-700 hover:bg-gray-500"
                        onClick={() => handleClearTurnOrder()}
                    >
                        Clear
                    </button>
                </ModalBody>
            </Modal>
        </>
    );
};

export default TurnOrderWidget;
